import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { carHouseService } from '../../../services/carHouseService';

const FilterPanel = ({ 
  filters, 
  onFiltersChange, 
  onExport, 
  isCollapsed, 
  onToggleCollapse 
}) => {
  const [dealerships, setDealerships] = useState([]);
  const [loadingDealerships, setLoadingDealerships] = useState(true);
  const [dealershipSearch, setDealershipSearch] = useState('');
  const [showDealershipDropdown, setShowDealershipDropdown] = useState(false);
  const dealershipDropdownRef = useRef(null);
  const handleMonthChange = (month) => {
    onFiltersChange({
      ...filters,
      selectedMonth: month
    });
  };

  const handleYearChange = (year) => {
    onFiltersChange({
      ...filters,
      selectedYear: year
    });
  };

  const handleDealershipChange = (value) => {
    onFiltersChange({
      ...filters,
      selectedDealership: value
    });
  };

  const handlePriceTypeChange = (type) => {
    onFiltersChange({
      ...filters,
      priceType: type
    });
  };

  const resetFilters = () => {
    const today = new Date();
    
    onFiltersChange({
      selectedMonth: (today.getMonth() + 1).toString(), // Current month (1-12)
      selectedYear: today.getFullYear().toString(), // Current year
      selectedDealership: 'all',
      priceType: 'gross'
    });
    
    // Reset dealership search
    setDealershipSearch('');
    setShowDealershipDropdown(false);
  };

  // Load dealerships from database
  const loadDealerships = async () => {
    try {
      setLoadingDealerships(true);
      const { data: dealershipsData, error } = await carHouseService.getAllCarHouses();
      
      if (error) {
        console.error('Error loading dealerships:', error);
        return;
      }

      // Transform dealership data for the search-select
      const transformedDealerships = [
        { id: 'all', name: 'Alle Autohäuser' },
        ...(dealershipsData || []).map(dealership => ({
          id: dealership.id,
          name: dealership.name
        }))
      ];

      setDealerships(transformedDealerships);
    } catch (err) {
      console.error('Error loading dealerships:', err);
    } finally {
      setLoadingDealerships(false);
    }
  };

  // Handle dealership selection
  const handleDealershipSelect = (dealership) => {
    onFiltersChange({
      ...filters,
      selectedDealership: dealership.id
    });
    setDealershipSearch(dealership.name);
    setShowDealershipDropdown(false);
  };

  // Load dealerships on component mount
  useEffect(() => {
    loadDealerships();
  }, []);

  // Handle outside clicks to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dealershipDropdownRef.current && !dealershipDropdownRef.current.contains(event.target)) {
        setShowDealershipDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Sync dealership search with selected dealership
  useEffect(() => {
    if (filters.selectedDealership === 'all') {
      setDealershipSearch('');
    } else {
      const selectedDealership = dealerships.find(d => d.id === filters.selectedDealership);
      if (selectedDealership) {
        setDealershipSearch(selectedDealership.name);
      }
    }
  }, [filters.selectedDealership, dealerships]);

  return (
    <div className={`bg-card border border-border rounded-lg transition-all duration-200 ${
      isCollapsed ? 'p-4' : 'p-6'
    }`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-text-primary">Filter & Export</h3>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
          className="lg:hidden"
        >
          <Icon name={isCollapsed ? "ChevronDown" : "ChevronUp"} size={20} />
        </Button>
      </div>
      {!isCollapsed && (
        <div className="space-y-4">
          {/* Month and Year Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">Monat</label>
              <select
                value={filters?.selectedMonth || ''}
                onChange={(e) => handleMonthChange(e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">Monat auswählen</option>
                <option value="1">Januar</option>
                <option value="2">Februar</option>
                <option value="3">März</option>
                <option value="4">April</option>
                <option value="5">Mai</option>
                <option value="6">Juni</option>
                <option value="7">Juli</option>
                <option value="8">August</option>
                <option value="9">September</option>
                <option value="10">Oktober</option>
                <option value="11">November</option>
                <option value="12">Dezember</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">Jahr</label>
              <select
                value={filters?.selectedYear || ''}
                onChange={(e) => handleYearChange(e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">Jahr auswählen</option>
                {Array.from({ length: 10 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <option key={year} value={year.toString()}>
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Dealership Selection */}
          {/* <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">Autohaus</label>
            <div className="relative" ref={dealershipDropdownRef}>
              <Input
                type="text"
                placeholder={loadingDealerships ? "Lade Autohäuser..." : "Autohaus suchen..."}
                value={dealershipSearch}
                onChange={(e) => {
                  const searchValue = e.target.value;
                  setDealershipSearch(searchValue);
                  if (!searchValue) {
                    onFiltersChange({
                      ...filters,
                      selectedDealership: 'all'
                    });
                  }
                }}
                onFocus={() => {
                  if (!loadingDealerships) {
                    setShowDealershipDropdown(true);
                  }
                }}
                className="w-full"
                disabled={loadingDealerships}
              />
              {showDealershipDropdown && !loadingDealerships && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-border rounded-md shadow-lg max-h-60 overflow-auto">
                  {dealerships
                    .filter(dealership => 
                      dealership.name.toLowerCase().includes((dealershipSearch || '').toLowerCase())
                    )
                    .map(dealership => (
                      <div
                        key={dealership.id}
                        className="px-3 py-2 hover:bg-muted cursor-pointer"
                        onClick={() => handleDealershipSelect(dealership)}
                      >
                        {dealership.name}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div> */}

          {/* Price Type Toggle */}
          {/* <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Preistyp
            </label>
            <div className="flex space-x-2">
              <Button
                variant={filters?.priceType === 'gross' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handlePriceTypeChange('gross')}
              >
                Brutto (inkl. MwSt.)
              </Button>
              <Button
                variant={filters?.priceType === 'net' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handlePriceTypeChange('net')}
              >
                Netto (ohne MwSt.)
              </Button>
            </div>
          </div> */}

          {/* Quick Month Selection */}
          <div className="pt-4 border-t border-border">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Schnellauswahl
            </label>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const today = new Date();
                  onFiltersChange({
                    ...filters,
                    selectedMonth: (today.getMonth() + 1).toString(),
                    selectedYear: today.getFullYear().toString()
                  });
                }}
              >
                Aktueller Monat
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const today = new Date();
                  const lastMonth = today.getMonth() === 0 ? 12 : today.getMonth();
                  const lastMonthYear = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
                  onFiltersChange({
                    ...filters,
                    selectedMonth: lastMonth.toString(),
                    selectedYear: lastMonthYear.toString()
                  });
                }}
              >
                Letzter Monat
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const today = new Date();
                  onFiltersChange({
                    ...filters,
                    selectedMonth: '12',
                    selectedYear: (today.getFullYear() - 1).toString()
                  });
                }}
              >
                Dezember Vorjahr
              </Button>
            </div>
          </div>

          {/* Action Buttons */}
          {/* <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={resetFilters}
              iconName="RotateCcw"
              iconPosition="left"
              className="flex-1"
            >
              Filter zurücksetzen
            </Button>
            <Button
              variant="default"
              onClick={onExport}
              iconName="Download"
              iconPosition="left"
              className="flex-1"
            >
              PDF Export
            </Button>
          </div> */}
        </div>
      )}
    </div>
  );
};

export default FilterPanel;